import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  FormHelperText,
  Grid,
  IconButton,
  Slide,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  addPacageExclusion,
  deletePacageExclusion,
  getPacageExclusion,
} from 'services/API/Administration';
import { Save, Close, Delete } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  inputNumberStrictLengthValidator,
  inputRequired,
  inputStringMinLengthValidator,
} from 'components/generics/Validators/validator';
import { validateFormData } from 'components/generics/Validators/form.validate';
import './PacageExclusion.scss';
import { useTranslation } from 'react-i18next';

const PacageExclusion = () => {
  const { t } = useTranslation();
  const [pacageExclusion, setPacageExclusion] = useState([]);
  const [filteredPacage, setFilteredPacage] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [openCardId, setOpenCardId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [numeroPacage, setNumeroPacage] = useState('');
  const [name, setName] = useState('');
  const [errorsFormDefault, setErrorsFormDefault] = useState({
    formError: false,
    name: {
      message: '',
      validator: [inputRequired, inputStringMinLengthValidator(2)],
    },
    numeroPacage: {
      message: '',
      validator: [inputNumberStrictLengthValidator(9)],
    },
  });

  const handleCancel = () => {
    setNumeroPacage('');
    setName('');
    setErrorsFormDefault({
      ...errorsFormDefault,
      formError: false,
      name: { ...errorsFormDefault.name, message: '' },
      numeroPacage: { ...errorsFormDefault.numeroPacage, message: '' },
    });
  };

  const handleValidate = async () => {
    const formData = { numeroPacage, name };
    errorsFormDefault.formError = false;
    setErrorsFormDefault({ ...validateFormData(formData, errorsFormDefault) });

    if (errorsFormDefault.formError) {
      return;
    }

    try {
      const newPacageExclusion = await addPacageExclusion(formData);
      setPacageExclusion([...pacageExclusion, newPacageExclusion?.data?.data]);
      toast.success(t('success.exploitationCodeAdded'));
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
    handleCancel();
  };

  useEffect(() => {
    getPacageExclusion()
      .then((res) => {
        setPacageExclusion(res.data.data);
      })
      .catch((err) => toast.error(t('errors.ERR_GET_PACAGE_EXCLUSION')));
  }, []);

  const filterPacage = (search) => {
    if (!search) {
      setFilteredPacage(pacageExclusion);
    } else {
      const searchLower = search.toLowerCase();
      setFilteredPacage(
        pacageExclusion.filter(
          (item) =>
            item.numeroPacage.includes(searchLower) ||
            item.name.toLowerCase().includes(searchLower),
        ),
      );
    }
  };

  useEffect(() => {
    if (searchValue !== '') {
      filterPacage(searchValue);
    } else {
      setFilteredPacage(pacageExclusion);
    }
  }, [pacageExclusion]);

  const handleDeletePacageExclusion = async (id) => {
    try {
      await deletePacageExclusion(id);
      toast.success(t('success.exploitationCodeDeleted'));
      setOpenCardId(null);
      setPacageExclusion(pacageExclusion.filter((excludedPacage) => excludedPacage.id !== id));
    } catch (error) {
      toast.error(t('errors.ERR_DELETE_PACAGE'));
    }
  };

  return (
    <div className="pacage_exclusion_container">
      <div>{t('label.excludedExploitationCodes')}</div>
      <Box className="pacage_exclusion_header">
        <Box>
          <Box className="pacage_exclusion_add_wrapper">
            <Button
              variant="contained"
              onClick={() => {
                setIsOpen(!isOpen);
                handleCancel();
              }}
              className="button_add"
            >
              {t('button.addCode')}
            </Button>

            <Slide in={isOpen} direction="right" timeout={1500} zIndex={1}>
              <Box className="add_inputs">
                <TextField
                  id="numeroPacage"
                  label={t('column.pacageNumber')}
                  size="small"
                  variant="outlined"
                  value={numeroPacage}
                  onChange={(e) => setNumeroPacage(e.target.value)}
                  error={Boolean(errorsFormDefault?.numeroPacage.message)}
                  className="input"
                />
                <TextField
                  id="name"
                  label={t('common.name')}
                  size="small"
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={Boolean(errorsFormDefault?.name.message)}
                  className="input"
                />
                <IconButton color="success" onClick={handleValidate} className="icon_button">
                  <Save />
                </IconButton>
                <IconButton color="error" onClick={handleCancel} className="icon_button">
                  <Close />
                </IconButton>
              </Box>
            </Slide>
          </Box>
          <FormHelperText error>
            {[errorsFormDefault?.numeroPacage.message, errorsFormDefault?.name.message]
              .filter(Boolean)
              .join(' | ')}
          </FormHelperText>
        </Box>

        <Autocomplete
          freeSolo
          size="small"
          className="autocomplete"
          options={pacageExclusion}
          getOptionLabel={(option) => `${option.numeroPacage} - ${option.name}`}
          filterOptions={(options, { inputValue }) => {
            const search = inputValue.toLowerCase();
            return options.filter(
              (option) =>
                option.numeroPacage.includes(search) || option.name.toLowerCase().includes(search),
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label={t('placeholder.searchByCodeOrName')} />
          )}
          inputValue={searchValue}
          onInputChange={(event, newValue, reason) => {
            setSearchValue(newValue);
            if (reason === 'input') {
              filterPacage(newValue);
            } else if (reason === 'clear') {
              setFilteredPacage(pacageExclusion);
            }
          }}
          onChange={(event, selectedOption) => {
            if (selectedOption) {
              setFilteredPacage([selectedOption]);
            } else {
              setFilteredPacage(pacageExclusion);
            }
          }}
        />
      </Box>
      <Grid container spacing={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2 }} className="grid_container">
        {filteredPacage.map((excludedPacage) => (
          <Grid key={excludedPacage.id} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <Card className="card">
              <CardHeader
                className="card_header"
                title={excludedPacage.name}
                slotProps={{
                  title: {
                    noWrap: true,
                    variant: 'body1',
                  },
                }}
                action={
                  <IconButton
                    color="error"
                    size="small"
                    className="icon_button"
                    onClick={() => setOpenCardId(excludedPacage.id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                }
              />
              <CardContent className="card_content">{excludedPacage.numeroPacage}</CardContent>
              <Collapse in={openCardId === excludedPacage.id}>
                <CardActions className="card_action">
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleDeletePacageExclusion(excludedPacage.id)}
                  >
                    {t('common.delete')}
                  </Button>
                  <Button variant="outlined" onClick={() => setOpenCardId(null)}>
                    {t('common.cancel')}
                  </Button>
                </CardActions>
              </Collapse>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default PacageExclusion;
